import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import styles from './style.module.scss'
import { Field, CheckboxInput } from '@Root/components'
import { API } from '@Root/API'
import { Link, useHistory, useParams } from 'react-router-dom'
import { BackButton } from '@Root/components'

/**
 * Build a recursive tree from flat dot-notation cognito_fields.
 */
const buildCognitoFieldTree = cognitoFieldsData => {
  if (!cognitoFieldsData || typeof cognitoFieldsData !== 'object') return []

  return Object.keys(cognitoFieldsData).map(groupName => {
    const fieldsMap = cognitoFieldsData[groupName]
    const allPaths = Object.keys(fieldsMap)

    const buildChildren = parentPath => {
      const children = []
      const seen = new Set()

      allPaths.forEach(path => {
        if (path === parentPath) return
        if (!path.startsWith(parentPath + '.')) return

        const rest = path.substring(parentPath.length + 1)
        const directChild = rest.split('.')[0]
        if (seen.has(directChild)) return
        seen.add(directChild)

        const childPath = parentPath + '.' + directChild
        const hasChildren = allPaths.some(p => p !== childPath && p.startsWith(childPath + '.'))
        const label = fieldsMap[childPath] || directChild.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

        if (hasChildren) {
          children.push({ path: childPath, label, type: 'group', children: buildChildren(childPath) })
        } else {
          children.push({ path: childPath, label, type: 'field' })
        }
      })

      return children
    }

    const label = fieldsMap[groupName] || groupName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    return { path: groupName, label, type: 'group', children: buildChildren(groupName) }
  })
}

const findFieldInTree = (nodes, path) => {
  for (const node of nodes) {
    if (node.path === path) return node
    if (node.children) {
      const found = findFieldInTree(node.children, path)
      if (found) return found
    }
  }
  return null
}

// Rules that require no params — just a boolean toggle
const SIMPLE_RULES = ['boolean', 'integer', 'trim', 'uppercase', 'lowercase', 'extract_after_dash', 'extract_before_dash', 'json_decode']

const TABLE_SLUGS = {
  student_applications: 'student-applications',
  person_addresses: 'person-addresses',
  student_details: 'student-details',
  language_proficiencies: 'language-proficiencies',
  contact_additional_fields: 'contact-additional-fields',
}

const STATUS_LABELS = {
  valid: 'Valid',
  missing: 'Missing',
  empty: 'No value',
  not_found: 'Not found',
  partial_match: 'Partial match',
  invalid: 'Invalid',
  unknown_field: 'Unknown field',
  no_sample: 'No sample',
}

const tableSlug = table => TABLE_SLUGS[table] || String(table || '').replace(/_/g, '-')

const statusClassName = status => {
  if (status === 'valid') return styles.statusValid
  if (status === 'empty' || status === 'no_sample') return styles.statusNeutral
  return styles.statusError
}

const unquoteSample = sample => {
  if (typeof sample === 'string' && sample.length >= 2 && sample.startsWith('"') && sample.endsWith('"')) {
    return sample.slice(1, -1)
  }
  return sample
}

const formatSampleOption = submission => {
  const raw = submission.created_at || ''
  const when = raw ? new Date(String(raw).replace(' ', 'T')) : null
  const label =
    when && !Number.isNaN(when.getTime())
      ? when.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      : raw
  return `#${submission.cognito_entry_id || submission.id} · ${label}`
}

const handbookTitle = handbook => `Type: ${handbook.type} · Storage: ${handbook.storage} · Transform: ${handbook.transform}`

const HandbookLink = ({ handbook }) => {
  if (!handbook || !handbook.slug || !handbook.field || !handbook.type) return null

  return (
    <Link
      className={styles.handbookLink}
      to={`/home/handbook/cognito/fields/${handbook.slug}#field-${encodeURIComponent(handbook.field)}`}
      title={handbookTitle(handbook)}
    >
      <span className={styles.typeHint}>{handbook.type}</span>
    </Link>
  )
}

const MappingCheck = ({ result }) => {
  if (!result) return null

  const showArrow = result.preview && result.preview !== unquoteSample(result.sample) && result.preview !== result.sample

  return (
    <div className={styles.mappingCheck}>
      <span className={`${styles.statusBadge} ${statusClassName(result.status)}`}>{STATUS_LABELS[result.status] || result.status}</span>
      {result.sample && <code>{result.sample}</code>}
      {showArrow && (
        <>
          <span className={styles.mappingArrow}>→</span>
          <code>{result.preview}</code>
        </>
      )}
      {result.message && result.message !== 'Valid' && <span className={styles.checkMessage}>{result.message}</span>}
    </div>
  )
}

/**
 * Component for editing transformation rules on a single mapping.
 * `value` = current transformation_rules object (e.g. { boolean: true, map_values: { "A": 1 } })
 * `onChange(newRules)` = callback
 * `availableRules` = the transformation_rules definition from the API
 */
const TransformationRulesEditor = ({ value, onChange, availableRules }) => {
  const rules = value || {}

  const toggleRule = ruleKey => {
    const next = { ...rules }
    if (ruleKey in next) {
      delete next[ruleKey]
    } else {
      // Set default value based on rule type
      if (SIMPLE_RULES.includes(ruleKey)) {
        next[ruleKey] = true
      } else if (ruleKey === 'date_format') {
        next[ruleKey] = { input: '', output: '' }
      } else if (ruleKey === 'map_values') {
        next[ruleKey] = {}
      } else if (ruleKey === 'default') {
        next[ruleKey] = ''
      } else if (ruleKey === 'array_to_string') {
        next[ruleKey] = ', '
      }
    }
    onChange(next)
  }

  const updateRuleParams = (ruleKey, params) => {
    onChange({ ...rules, [ruleKey]: params })
  }

  // map_values helpers
  const mapEntries = useMemo(() => {
    const mv = rules.map_values
    if (!mv || typeof mv !== 'object') return [{ key: '', value: '' }]
    const entries = Object.entries(mv).map(([k, v]) => ({ key: k, value: String(v) }))
    return entries.length > 0 ? entries : [{ key: '', value: '' }]
  }, [rules.map_values])

  const updateMapEntry = (index, field, val) => {
    const entries = [...mapEntries]
    entries[index] = { ...entries[index], [field]: val }
    const obj = {}
    entries.forEach(e => {
      if (e.key.trim() !== '') obj[e.key] = isNaN(e.value) ? e.value : Number(e.value)
    })
    updateRuleParams('map_values', obj)
  }

  const addMapEntry = () => {
    const obj = { ...rules.map_values, '': '' }
    updateRuleParams('map_values', obj)
  }

  const removeMapEntry = index => {
    const entries = [...mapEntries]
    entries.splice(index, 1)
    const obj = {}
    entries.forEach(e => {
      if (e.key.trim() !== '') obj[e.key] = isNaN(e.value) ? e.value : Number(e.value)
    })
    updateRuleParams('map_values', obj)
  }

  return (
    <div className={styles.rulesEditor}>
      {Object.entries(availableRules).map(([ruleKey, ruleDef]) => {
        const isActive = ruleKey in rules
        return (
          <div key={ruleKey} className={`${styles.ruleRow} ${isActive ? styles.ruleActive : ''}`}>
            <label className={styles.ruleCheckbox}>
              <input type='checkbox' checked={isActive} onChange={() => toggleRule(ruleKey)} />
              <span className={styles.ruleName}>{ruleDef.name}</span>
              <span className={styles.ruleDesc}>{ruleDef.description}</span>
            </label>

            {isActive && ruleKey === 'date_format' && (
              <div className={styles.ruleParams}>
                <div className={styles.ruleParamRow}>
                  <label>Input format:</label>
                  <input
                    type='text'
                    placeholder='e.g. Y-m-d'
                    value={rules.date_format?.input || ''}
                    onChange={e => updateRuleParams('date_format', { ...rules.date_format, input: e.target.value })}
                    className={styles.ruleInput}
                  />
                </div>
                <div className={styles.ruleParamRow}>
                  <label>Output format:</label>
                  <input
                    type='text'
                    placeholder='e.g. d-m-Y'
                    value={rules.date_format?.output || ''}
                    onChange={e => updateRuleParams('date_format', { ...rules.date_format, output: e.target.value })}
                    className={styles.ruleInput}
                  />
                </div>
              </div>
            )}

            {isActive && ruleKey === 'map_values' && (
              <div className={styles.ruleParams}>
                {mapEntries.map((entry, i) => (
                  <div key={i} className={styles.mapEntryRow}>
                    <input
                      type='text'
                      placeholder='Source value'
                      value={entry.key}
                      onChange={e => updateMapEntry(i, 'key', e.target.value)}
                      className={styles.ruleInput}
                    />
                    <span className={styles.mapArrow}>&rarr;</span>
                    <input
                      type='text'
                      placeholder='Target value'
                      value={entry.value}
                      onChange={e => updateMapEntry(i, 'value', e.target.value)}
                      className={styles.ruleInput}
                    />
                    {mapEntries.length > 1 && (
                      <button type='button' className={styles.removeEntryBtn} onClick={() => removeMapEntry(i)}>
                        &times;
                      </button>
                    )}
                  </div>
                ))}
                <button type='button' className={styles.addEntryBtn} onClick={addMapEntry}>
                  + Add value
                </button>
              </div>
            )}

            {isActive && ruleKey === 'default' && (
              <div className={styles.ruleParams}>
                <div className={styles.ruleParamRow}>
                  <label>Default value:</label>
                  <input
                    type='text'
                    placeholder='Value when empty'
                    value={typeof rules.default === 'string' ? rules.default : ''}
                    onChange={e => updateRuleParams('default', e.target.value)}
                    className={styles.ruleInput}
                  />
                </div>
              </div>
            )}

            {isActive && ruleKey === 'array_to_string' && (
              <div className={styles.ruleParams}>
                <div className={styles.ruleParamRow}>
                  <label>Separator:</label>
                  <input
                    type='text'
                    placeholder=', '
                    value={typeof rules.array_to_string === 'string' ? rules.array_to_string : ', '}
                    onChange={e => updateRuleParams('array_to_string', e.target.value)}
                    className={styles.ruleInput}
                  />
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/**
 * Renders a compact read-only view of active transformation rules.
 */
const TransformationRulesBadges = ({ rules, availableRules }) => {
  if (!rules || Object.keys(rules).length === 0) return null

  return (
    <div className={styles.rulesBadges}>
      {Object.entries(rules).map(([key, params]) => {
        const def = availableRules[key]
        const label = def?.name || key
        let detail = ''

        if (key === 'map_values' && typeof params === 'object') {
          detail = Object.entries(params)
            .map(([k, v]) => `${k} → ${v}`)
            .join(', ')
        } else if (key === 'date_format' && typeof params === 'object') {
          detail = `${params.input || '?'} → ${params.output || '?'}`
        } else if (key === 'default') {
          detail = `"${params}"`
        } else if (key === 'array_to_string') {
          detail = `sep: "${params}"`
        }

        return (
          <span key={key} className={styles.ruleBadge} title={detail || label}>
            {label}
            {detail && <span className={styles.ruleBadgeDetail}> ({detail})</span>}
          </span>
        )
      })}
    </div>
  )
}

export const MappingsCognitoForms = () => {
  const history = useHistory()
  const { id } = useParams()
  const [availableFields, setAvailableFields] = useState({})
  const [transformationRules, setTransformationRules] = useState({})
  const [cognitoFields, setCognitoFields] = useState([])
  const [existingMappings, setExistingMappings] = useState([])
  const [cognitoSamples, setCognitoSamples] = useState({})
  const [preview, setPreview] = useState(null)
  const [sampleId, setSampleId] = useState(null)
  const [handbookByTable, setHandbookByTable] = useState({})
  const handbookRequests = useRef({})
  const previewRequest = useRef(0)
  const seenFormId = useRef(id)
  if (seenFormId.current !== id) {
    seenFormId.current = id
    if (sampleId !== null) setSampleId(null)
  }
  const [expandedCognitoFields, setExpandedCognitoFields] = useState({})
  const [selectedCognitoField, setSelectedCognitoField] = useState(null)
  const [selectedCognitoFieldSample, setSelectedCognitoFieldSample] = useState('')
  const [formData, setFormData] = useState({
    teid_table: '',
    teid_field: '',
    is_required: false,
    transformation_rules: {},
  })
  const [teidFields, setTeidFields] = useState([])
  const [errors, setErrors] = useState({})
  const [isAutoMapping, setIsAutoMapping] = useState(false)
  const [showCreateRules, setShowCreateRules] = useState(false)
  const [editingMappingId, setEditingMappingId] = useState(null)
  const [editingRules, setEditingRules] = useState({})
  const [isSavingRules, setIsSavingRules] = useState(false)

  const loadPreview = async (submissionId = sampleId) => {
    const requestId = ++previewRequest.current
    try {
      const params = { cognito_form_id: id }
      if (submissionId) params.submission_id = submissionId
      const { data } = await API.cognitoForms.previewFieldMappings(params)
      if (requestId === previewRequest.current) setPreview(data)
    } catch (error) {
      console.error('Error previewing mappings:', error)
    }
  }

  const refreshMappings = async () => {
    const response = await API.cognitoForms.getFieldMappings(id)
    const mappings = Array.isArray(response.data) ? response.data : Array.isArray(response.data?.data) ? response.data.data : []
    setExistingMappings(mappings)
    await loadPreview()
  }

  const handleAutoMap = async () => {
    setIsAutoMapping(true)
    try {
      const { data } = await API.cognitoForms.autoMapFields({ cognito_form_id: parseInt(id) })
      await refreshMappings()
      alert(data.message || `Auto-mapped ${data.created} fields`)
    } catch (error) {
      console.error('Error auto-mapping fields:', error)
      alert('Failed to auto-map fields')
    } finally {
      setIsAutoMapping(false)
    }
  }

  useEffect(() => {
    const fetchAvailableFields = async () => {
      try {
        const [{ data }, response] = await Promise.all([API.cognitoForms.getAvailableFields({ cognito_form_id: id }), API.cognitoForms.getFieldMappings(id)])
        setAvailableFields(data.teid_fields || {})
        setTransformationRules(data.transformation_rules || {})
        setCognitoSamples(data.cognito_samples || {})

        if (data.cognito_fields && typeof data.cognito_fields === 'object') {
          setCognitoFields(buildCognitoFieldTree(data.cognito_fields))
        } else {
          setCognitoFields([])
        }

        const mappings = Array.isArray(response.data) ? response.data : Array.isArray(response.data?.data) ? response.data.data : []
        setExistingMappings(mappings)
      } catch (error) {
        console.error('Error fetching available fields:', error)
      }
    }
    fetchAvailableFields()
  }, [id])

  useEffect(() => {
    const requestId = ++previewRequest.current
    let cancelled = false

    const run = async () => {
      try {
        const params = { cognito_form_id: id }
        if (sampleId) params.submission_id = sampleId
        const { data } = await API.cognitoForms.previewFieldMappings(params)
        if (!cancelled && requestId === previewRequest.current) setPreview(data)
      } catch (error) {
        if (!cancelled) console.error('Error previewing mappings:', error)
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [id, sampleId])

  useEffect(() => {
    const table = formData.teid_table
    if (!table || handbookRequests.current[table]) return undefined

    handbookRequests.current[table] = true
    API.handbook
      .getCognitoFields(tableSlug(table))
      .then(({ data }) => {
        const fields = {}
        ;(data?.fields || []).forEach(field => {
          fields[field.key] = field
        })
        setHandbookByTable(prev => ({ ...prev, [table]: fields }))
      })
      .catch(() => {
        handbookRequests.current[table] = false
      })

    return undefined
  }, [formData.teid_table])

  useEffect(() => {
    if (formData.teid_table && availableFields[formData.teid_table]) {
      const fields = Object.keys(availableFields[formData.teid_table])
        .map(key => ({
          value: key,
          label: `${key} - ${availableFields[formData.teid_table][key]}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
      setTeidFields(fields)
    } else {
      setTeidFields([])
      setFormData(prev => ({ ...prev, teid_field: '' }))
    }
  }, [formData.teid_table, availableFields])

  const toggleCognitoField = path => {
    setExpandedCognitoFields(prev => ({
      ...prev,
      [path]: !prev[path],
    }))
  }

  const handleCognitoFieldSelect = field => {
    setSelectedCognitoField(field)
  }

  useEffect(() => {
    if (!selectedCognitoField) {
      setSelectedCognitoFieldSample('')
      return
    }

    const sample = cognitoSamples[selectedCognitoField]
    if (sample === undefined || sample === null || sample === '') {
      setSelectedCognitoFieldSample('')
      return
    }

    setSelectedCognitoFieldSample(typeof sample === 'string' ? sample : JSON.stringify(sample))
  }, [selectedCognitoField, cognitoSamples])

  const handleFormChange = field => value => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleCreateMapping = async () => {
    const newErrors = {}
    if (!selectedCognitoField) newErrors.cognito_field = 'Cognito Field is required'
    if (!formData.teid_table) newErrors.teid_table = 'TEID Table is required'
    if (!formData.teid_field) newErrors.teid_field = 'TEID Field is required'

    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) return

    const cognitoFieldInfo = selectedCognitoField ? findFieldInTree(cognitoFields, selectedCognitoField) : null

    // Clean empty transformation rules
    const cleanRules = Object.keys(formData.transformation_rules).length > 0 ? formData.transformation_rules : null

    try {
      const payload = {
        cognito_form_id: parseInt(id),
        cognito_field_name: selectedCognitoField || '',
        cognito_field_label: cognitoFieldInfo?.label || null,
        cognito_field_type: cognitoFieldInfo?.type || null,
        teid_table_name: formData.teid_table,
        teid_field_name: formData.teid_field,
        is_required: formData.is_required,
        transformation_rules: cleanRules,
      }

      await API.cognitoForms.createFieldMapping(payload)
      await refreshMappings()

      setSelectedCognitoField(null)
      setFormData({ teid_table: '', teid_field: '', is_required: false, transformation_rules: {} })
      setErrors({})
    } catch (error) {
      console.error('Error creating mapping:', error)
    }
  }

  const handleDeleteMapping = async mappingId => {
    try {
      await API.cognitoForms.deleteFieldMapping(mappingId)
      setExistingMappings(prev => prev.filter(m => m.id !== mappingId))
      await loadPreview()
    } catch (error) {
      console.error('Error deleting mapping:', error)
    }
  }

  const handleEditRules = mapping => {
    setEditingMappingId(mapping.id)
    setEditingRules(mapping.transformation_rules || {})
  }

  const handleCancelEditRules = () => {
    setEditingMappingId(null)
    setEditingRules({})
  }

  const handleSaveRules = async () => {
    setIsSavingRules(true)
    try {
      const cleanRules = Object.keys(editingRules).length > 0 ? editingRules : null
      await API.cognitoForms.updateFieldMapping(editingMappingId, { transformation_rules: cleanRules })
      await refreshMappings()
      setEditingMappingId(null)
      setEditingRules({})
    } catch (error) {
      console.error('Error saving transformation rules:', error)
    } finally {
      setIsSavingRules(false)
    }
  }

  const getTeidTableOptions = () => {
    const studentSubTables = ['student_details', 'disabilities', 'language_proficiencies']
    return Object.keys(availableFields).map(key => {
      const isStudentSub = studentSubTables.includes(key)
      const baseLabel = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      return {
        value: key,
        label: isStudentSub ? `Students \u2192 ${baseLabel}` : baseLabel,
      }
    })
  }

  const renderNode = useCallback(
    node => {
      if (node.type === 'group') {
        const isSelected = selectedCognitoField === node.path
        return (
          <div key={node.path} className={styles.treeItem}>
            <div className={`${styles.treeGroup} ${isSelected ? styles.selected : ''}`}>
              <input
                type='radio'
                name='cognitoField'
                checked={isSelected}
                onChange={() => handleCognitoFieldSelect(node.path, '')}
                className={styles.radioInput}
              />
              <span className={styles.treeIcon} onClick={() => toggleCognitoField(node.path)}>
                {expandedCognitoFields[node.path] ? '▼' : '▶'}
              </span>
              <span className={styles.treeLabel} onClick={() => toggleCognitoField(node.path)}>
                {node.label}
              </span>
              <span className={styles.treeType}>(group)</span>
            </div>
            {expandedCognitoFields[node.path] && node.children && node.children.length > 0 && (
              <div className={styles.treeChildren}>{node.children.map(child => renderNode(child))}</div>
            )}
          </div>
        )
      }

      return (
        <div
          key={node.path}
          className={`${styles.treeField} ${selectedCognitoField === node.path ? styles.selected : ''}`}
          onClick={() => handleCognitoFieldSelect(node.path, '')}
        >
          <input
            type='radio'
            name='cognitoField'
            checked={selectedCognitoField === node.path}
            onChange={() => handleCognitoFieldSelect(node.path, '')}
            className={styles.radioInput}
          />
          <span className={styles.fieldName}>{node.label}</span>
          <span className={styles.fieldType}>(field)</span>
        </div>
      )
    },
    [expandedCognitoFields, selectedCognitoField]
  )

  const previewById = {}
  ;(preview?.results || []).forEach(result => {
    previewById[result.mapping_id] = result
  })
  const selectedSample = sampleId || preview?.sample?.id || ''

  const renderCognitoFieldTree = () => {
    if (!Array.isArray(cognitoFields) || cognitoFields.length === 0) {
      return <div className={styles.cognitoFieldTree}>No fields available</div>
    }
    return (
      <div className={`${errors?.cognito_field ? styles.errorField : ''} ${styles.cognitoFieldTree}`}>
        {cognitoFields.map(node => renderNode(node))}
      </div>
    )
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <div className={styles.leftPanel}>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Create Field Mapping</h3>
            </div>

            <div className={styles.formSection}>
              <label className={styles.label}>
                Cognito Field<span className={styles.required}>*</span>
              </label>
              {renderCognitoFieldTree()}
              {errors?.cognito_field && <div className={styles.errorMessage}>{errors?.cognito_field}</div>}
            </div>

            {selectedCognitoField && (
              <div className={styles.selectedFieldInfo}>
                <div className={styles.selectedField}>
                  <strong>Selected:</strong> <code>{selectedCognitoField}</code>
                </div>
                {selectedCognitoFieldSample && (
                  <div className={styles.sampleValue}>
                    <strong>Sample value:</strong> <code>{selectedCognitoFieldSample}</code>
                  </div>
                )}
              </div>
            )}

            <div className={styles.arrowDown}>↓</div>

            <div className={styles.formSection}>
              <Field
                label='TEID Table*'
                classNames={['transparent']}
                style={{ width: '100%' }}
                value={formData.teid_table}
                onChange={handleFormChange('teid_table')}
                placeholder='Select table...'
                isRequired={true}
                field='select'
                options={getTeidTableOptions()}
                search={true}
                styleWrapper={{ gridTemplateColumns: '150px 1fr' }}
                error={errors?.teid_table}
              />
            </div>

            <div className={styles.formSection}>
              <Field
                label='TEID Field*'
                classNames={['transparent']}
                style={{ width: '100%' }}
                value={formData.teid_field}
                onChange={handleFormChange('teid_field')}
                placeholder='Select field...'
                isRequired={true}
                field='select'
                options={teidFields}
                search={true}
                searchPattern={/[_\-.]/g}
                isDisabled={!formData.teid_table}
                styleWrapper={{ gridTemplateColumns: '150px 1fr' }}
                error={errors?.teid_field}
              />
              {formData.teid_field && handbookByTable[formData.teid_table] && handbookByTable[formData.teid_table][formData.teid_field] && (
                <div className={styles.fieldHint}>
                  <HandbookLink
                    handbook={{
                      slug: tableSlug(formData.teid_table),
                      field: formData.teid_field,
                      type: handbookByTable[formData.teid_table][formData.teid_field].type,
                      storage: handbookByTable[formData.teid_table][formData.teid_field].storage,
                      transform: handbookByTable[formData.teid_table][formData.teid_field].transform,
                    }}
                  />
                  <span>{handbookByTable[formData.teid_table][formData.teid_field].storage}</span>
                </div>
              )}
            </div>

            <div className={styles.formSection}>
              <CheckboxInput isChecked={formData.is_required} changeHandler={handleFormChange('is_required')} label={{ text: 'Required field' }} />
            </div>

            <div className={styles.formSection}>
              <div
                className={styles.collapsibleHeader}
                onClick={() => setShowCreateRules(prev => !prev)}
              >
                <span className={styles.treeIcon}>{showCreateRules ? '▼' : '▶'}</span>
                <span className={styles.label} style={{ marginBottom: 0 }}>Transformation Rules</span>
                {Object.keys(formData.transformation_rules).length > 0 && (
                  <span className={styles.rulesCount}>{Object.keys(formData.transformation_rules).length} active</span>
                )}
              </div>
              {showCreateRules && (
                <TransformationRulesEditor
                  value={formData.transformation_rules}
                  onChange={rules => setFormData(prev => ({ ...prev, transformation_rules: rules }))}
                  availableRules={transformationRules}
                />
              )}
            </div>

            <div className={styles.actions}>
              <BackButton text={'Cancel'} clickHandler={() => history.goBack()} />{' '}
              <button className={styles.createButton} onClick={handleCreateMapping}>
                Create Mapping
              </button>
            </div>
          </div>
        </div>

        <div className={styles.rightPanel}>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Existing Mappings</h3>
              <div className={styles.headerTools}>
                {preview?.summary && existingMappings.length > 0 && (
                  <div className={styles.summaryChips}>
                    <span className={`${styles.statusBadge} ${styles.statusValid}`}>{preview.summary.valid} valid</span>
                    <span className={`${styles.statusBadge} ${styles.statusError}`}>{preview.summary.error} errors</span>
                    <span className={`${styles.statusBadge} ${styles.statusNeutral}`}>{preview.summary.neutral} no value</span>
                  </div>
                )}
                {preview?.submissions?.length > 0 && existingMappings.length > 0 && (
                  <select
                    className={styles.sampleSelect}
                    value={selectedSample === '' ? '' : String(selectedSample)}
                    onChange={event => setSampleId(event.target.value ? Number(event.target.value) : null)}
                  >
                    {preview.submissions.map(submission => (
                      <option key={submission.id} value={String(submission.id)}>
                        {formatSampleOption(submission)}
                      </option>
                    ))}
                  </select>
                )}
                <button className={`${styles.createButton} ${styles.headerButton}`} onClick={handleAutoMap} disabled={isAutoMapping}>
                  {isAutoMapping ? 'Mapping...' : 'Auto-map Fields'}
                </button>
              </div>
            </div>
            {existingMappings.length > 0 && preview && !preview.sample && (
              <div className={styles.previewNote}>Submit a test form to validate mappings</div>
            )}
            <div className={styles.mappingsList}>
              {existingMappings.length > 0 ? (
                existingMappings.map(mapping => (
                  <div key={mapping.id} className={styles.mappingItem}>
                    <div className={styles.mappingInfo}>
                      <div className={styles.mappingText}>
                        <code>{mapping.cognito_field_name || mapping.cognito_field}</code>
                        <span className={styles.mappingArrow}>→</span>
                        <code>
                          {mapping.teid_table_name || mapping.teid_table}.{mapping.teid_field_name || mapping.teid_field}
                        </code>
                        {mapping.is_required && <span className={styles.requiredBadge}>Required</span>}
                        <HandbookLink handbook={previewById[mapping.id] && previewById[mapping.id].handbook} />
                      </div>
                      <TransformationRulesBadges rules={mapping.transformation_rules} availableRules={transformationRules} />
                      <MappingCheck result={previewById[mapping.id]} />
                    </div>

                    <div className={styles.mappingActions}>
                      <button
                        className={styles.editButton}
                        onClick={() => handleEditRules(mapping)}
                        title='Edit transformation rules'
                      >
                        &#9881;
                      </button>
                      <button className={styles.deleteButton} onClick={() => handleDeleteMapping(mapping.id)} title='Delete'>
                        ✕
                      </button>
                    </div>

                    {editingMappingId === mapping.id && (
                      <div className={styles.editRulesPanel}>
                        <div className={styles.editRulesHeader}>
                          <strong>Transformation Rules</strong>
                        </div>
                        <TransformationRulesEditor
                          value={editingRules}
                          onChange={setEditingRules}
                          availableRules={transformationRules}
                        />
                        <div className={styles.editRulesActions}>
                          <button className={styles.saveButton} onClick={handleSaveRules} disabled={isSavingRules}>
                            {isSavingRules ? 'Saving...' : 'Save'}
                          </button>
                          <button className={styles.cancelButton} onClick={handleCancelEditRules}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className={styles.emptyState}>No mappings yet</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
